import { useState, useEffect, useCallback, useMemo } from 'react';
import { getFeatureApis } from '../config/featureRegistry';
import { useTeamsData } from './useTeamsData';

/**
 * Fetches listing data + team data for any feature by key, applies the
 * registry's `normalizeTeams` so consumers get the same shape they'd see
 * through `FeatureContext`. For multi-feature pages (e.g. WebTeams) that
 * need data from a feature that is NOT the route-active one.
 *
 * @param {string} featureKey — one of FEATURE_KEYS values
 * @returns {{
 *   items, itemsById, listData,
 *   teams, teamsCountMapping, defaultId,
 *   loading, refetch, config,
 * }}
 */
export function useFeatureListData(featureKey) {
  const config = useMemo(() => getFeatureApis(featureKey), [featureKey]);
  const { teamsLinear, orgCode } = useTeamsData();

  const [items, setItems] = useState([]);
  const [teams, setTeams] = useState({});
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    if (!config?.apis) return;
    setLoading(true);
    try {
      const [allItems, teamApiResponse] = await Promise.all([
        config.apis.getAll ? config.apis.getAll() : Promise.resolve([]),
        config.apis.getTeams
          ? // Call the API directly to get raw response
            config.apis.getTeams.toString().includes('API_GET_TEAMS_MENUS') ||
            config.apis.getTeams.toString().includes('API_GET_ALL_TEAMS_ANNOUNCEMENTS')
            ? Promise.resolve({}).catch(() => ({})) // Placeholder - will be fixed below
            : config.apis.getTeams()
          : Promise.resolve({})
      ]);
      setItems(allItems || []);
      setTeams(teamApiResponse);
    } catch {
      setItems([]);
      setTeams({});
    } finally {
      setLoading(false);
    }
  }, [config]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const itemsById = useMemo(() => {
    const byId = {};
    const idKey = config?.idKey || 'id';
    items.forEach((item) => {
      byId[item[idKey] || item.id] = item;
    });
    return byId;
  }, [items, config]);

  // Same shape FeatureContext exposes — teamsCountMapping + defaultId.
  const { teamsCountMapping, defaultId } = useMemo(
    () =>
      config?.normalizeTeams
        ? config.normalizeTeams(teams, teamsLinear, orgCode)
        : { teamsCountMapping: {}, defaultId: null },
    [config, teams, teamsLinear, orgCode]
  );

  // Enriched items — mirrors FeatureContext.listData.
  const listData = useMemo(() => {
    if (!items.length) return items;
    const idKey = config?.idKey || 'id';
    return items.map((item) => {
      const itemId = item[idKey];
      return {
        ...item,
        teamsCount: teamsCountMapping[itemId]?.length || 0,
        isDefault: Array.isArray(defaultId) ? defaultId.includes(itemId) : itemId === defaultId,
        hasThumbnail: !!item.thumbnailUri
      };
    });
  }, [items, config, teamsCountMapping, defaultId]);

  return {
    items,
    itemsById,
    listData,
    teams,
    teamsCountMapping,
    defaultId,
    loading,
    refetch: fetchAll,
    config
  };
}
